import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface FirebaseUser {
  id?: number;
  firebase_uid: string;
  email: string;
  display_name: string;
  photo_url?: string;
  role: string;
  is_active: string;
  permission: string;
}

interface AuthContextType {
  currentUser: User | null;
  dbUser: FirebaseUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [dbUser, setDbUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user exists in your database
  const checkUserInDatabase = async (firebaseUser: User): Promise<FirebaseUser | null> => {
    try {
      console.log('Checking user in database:', firebaseUser.email);
      const idToken = await firebaseUser.getIdToken();
      // Dynamic API URL based on current hostname
      const getApiUrl = () => {
        if (process.env.REACT_APP_API_URL) {
          return process.env.REACT_APP_API_URL;
        }
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        
        // Check if this is a Tailscale domain and use port 8443
        if (hostname.includes('.ts.net')) {
          return `${protocol}//${hostname}:8443/api`;
        }
        
        // Use port 5000 for local development
        return `${protocol}//${hostname}:5000/api`;
      };
      const apiUrl = getApiUrl();
      console.log('API URL:', apiUrl);
      
      const requestBody = {
        firebase_uid: firebaseUser.uid,
        email: firebaseUser.email,
        display_name: firebaseUser.displayName,
        photo_url: firebaseUser.photoURL
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('User data from database:', userData);
        return userData;
      } else {
        const errorText = await response.text();
        console.error('User not authorized in database. Response:', errorText);
        return null;
      }
    } catch (error) {
      console.error('Error checking user in database:', error);
      return null;
    }
  };

  const login = async () => {
    try {
      console.log('Starting login process...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log('Firebase login successful for user:', user.email);
      
      // Check if user is authorized in your database
      const dbUserData = await checkUserInDatabase(user);
      if (!dbUserData) {
        console.log('User not found in database, signing out...');
        await signOut(auth);
        throw new Error('You are not authorized to access this application');
      }
      
      console.log('Setting dbUser data:', dbUserData);
      setDbUser(dbUserData);
      // Store user email for API authentication
      localStorage.setItem('userEmail', user.email || '');
      console.log('Login process completed successfully');
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setDbUser(null);
      // Clear stored user email
      localStorage.removeItem('userEmail');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed. User:', user?.email || 'null');
      setCurrentUser(user);
      
      if (user) {
        // User is signed in, check if they're in database
        console.log('User signed in, checking database...');
        const dbUserData = await checkUserInDatabase(user);
        console.log('Database check result:', dbUserData);
        setDbUser(dbUserData);
        if (dbUserData && user.email) {
          localStorage.setItem('userEmail', user.email);
        }
      } else {
        // User is signed out
        console.log('User signed out');
        setDbUser(null);
      }
      
      setLoading(false);
      console.log('Auth loading set to false');
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    dbUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
